import { FulfillmentOption } from 'common/Constants';
import { urlDateFormat } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { DateTime } from 'luxon';
import QueryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteChildrenProps } from 'react-router';
import { useMultiCartScrollHeader } from 'shef-web/src/pages/consumer/order/components/MultiCart/MultiCartHeader';
import emptyCart from 'src/assets/svg/order/empty-cart.svg';
import { useIsDishFirst } from 'src/dish-first/useIsDishFirst';
import { ConsumerFoodItemFragment, OrderShefFragment } from 'src/gqlReactTypings.generated.d';
import { MobileStickyFooter } from 'src/shared/design-system/ScrollablePane/MobileStickyFooter';
import { Paragraph } from 'src/shared/design-system/Text';
import Notifications from 'src/shared/Notifications';
import { CONSUMER_ORDER_TYPE_QUERY_PARAM_JUST_EDITED, Routes } from 'src/shared/Routes';
import { getIdSlug, getIdSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useDispatch, useSelector } from 'src/store';
import { setAddOnItemQuantity, setLineItemQuantity } from 'src/store/cart/actions';
import {
  selectActiveCartItemCount,
  selectActiveCartLineItemCount,
  selectAvailableCartItemCount,
  selectAvailableCartOrders,
  selectCart,
  selectMultiShefCartSubtotal,
  selectShefCartIsEmpty,
  selectShefDeliveryDateCapacityInfo,
  selectShefLineItems,
  selectSingleShefCartSubtotal,
} from 'src/store/cart/selectors';
import { CartActionLocation, CartFoodItem, ServerAddOnItem } from 'src/store/cart/types';
import { DEFAULT_CAPACITY_INFO } from 'src/store/cart/utils/capacity';
import { startCheckout } from 'src/store/checkout';
import styled, { css } from 'styled-components';
import { match as matchPattern } from 'ts-pattern';
import { Colors, ScreenWidth } from 'web-common/src/shared/Constants';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { useCachedZipCode } from '../../../../user-preferences/useCachedZipCode';
import { useDishFistCartPositionContext } from '../../components/ConsumerHeaderWrapper';
import { ConsumerOrderCartButton } from './ConsumerOrderCartButton';
import { ConsumerOrderCartInfo } from './ConsumerOrderCartInfo';
import { TrialShefWarning } from './ConsumerOrderCartTrialShefWarning';
import { ConsumerOrderFreeDeliveryModal } from './ConsumerOrderFreeDeliveryModal';
import { DeliveryDateMismatchWarning } from './DeliveryDateMismatchWarning';
import { ConsumerOrderMultiCartInfo } from './MultiCart/ConsumerOrderMultiCartInfo';
import { SuggestedSides } from './suggestions/SuggestedSides';

export const cartDropShadow = css`
  filter: drop-shadow(-16px 15px 20px rgba(0, 0, 0, 0.05));
`;

interface IProps {
  foodItems: ConsumerFoodItemFragment[];
  shef: OrderShefFragment;
  checkout: (checkout?: boolean) => any;
  deliveryDate?: DateTime;
  match: RouteChildrenProps['match'];
  location: RouteChildrenProps['location'];
  isEditingOrder: boolean;
  setDeliveryDate: (deliveryDate: DateTime) => void;
}

const CartCol = styled.div<{ isMultiCart: boolean; showShadow: boolean }>`
  top: ${ifProp('isMultiCart', '48px', '0px')};
  position: -webkit-sticky;
  position: sticky;
  background-color: ${Colors.WHITE};
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100vh;
  width: 100%;
  flex-direction: column;
  display: none;

  @media (min-width: ${(props) => (props.isMultiCart ? ScreenWidth.SMALL : ScreenWidth.MEDIUM)}) {
    display: flex;
  }

  ${ifProp('showShadow', cartDropShadow)}
`;

const DesktopCart = styled.div`
  padding-right: 0;
  padding-left: 0;
`;

const EmptyCart = styled.div`
  padding-top: 50px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    display: none;
  }
`;

const CheckoutButtonContainer = styled.div`
  display: flex;
  padding: 14px;
`;

const SuggestedSidesContainer = styled.div`
  // Ensure that the negative margin within suggested sides matches the outside padding
  --container-padding: 14px;
  padding: 0 var(--container-padding);
`;

export function isWorthUpsellingFreeDelivery(subtotal: number, freeDeliveryMinimum: number) {
  // Is there free delivery for this zip?
  if (!freeDeliveryMinimum) {
    return false;
  }

  // If they are within $7 of free delivery, we prompt them (most mains are >$7, so would only require adding one dish)
  const minSubTotalForFreeDeliveryUpsellDialog = freeDeliveryMinimum - 700;
  return subtotal >= minSubTotalForFreeDeliveryUpsellDialog && subtotal < freeDeliveryMinimum;
}

export const ConsumerOrderCart: React.FC<IProps> = ({
  shef,
  checkout,
  deliveryDate,
  match,
  location,
  foodItems,
  isEditingOrder,
  setDeliveryDate,
}) => {
  const orderId = parseInt(getIdSlug(match), 10);
  const deliveryUrlDate = deliveryDate?.toFormat(urlDateFormat);
  const cartRef = useRef(false);

  const { publicName, shefProfile } = shef;
  const [showModal, setShowModal] = useState<boolean>();

  const dispatch = useDispatch();
  const { isDishFirst } = useIsDishFirst();
  const {
    isMultiCart,
    activeCartItemCount,
    hasItemsInCart,
    subtotal,
    shefLineItems,
    checkoutShefIds,
    isCurrentShefCartEmpty,
    isOrderingMoreThanDeliveryDateCapacity,
  } = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    const orders = selectAvailableCartOrders(cart);
    const shefDeliveryDateCapacityInfo = deliveryUrlDate
      ? selectShefDeliveryDateCapacityInfo(cart, cart.activeCartShefId, isEditingOrder, deliveryUrlDate)
      : DEFAULT_CAPACITY_INFO;
    const orderShefIds = orders.map((o) => o.shef.id);

    return {
      isMultiCart: cart.isMultiCart,
      // multi-cart has no area to show suggested side items
      shefLineItems: cart.isMultiCart ? [] : selectShefLineItems(cart, shef.id),
      // if multi-cart it will be the total number of shefs being ordered from
      // else it will be the total line items being ordered from the currently active shef
      activeCartItemCount: cart.isMultiCart ? selectAvailableCartItemCount(cart) : selectActiveCartItemCount(cart),
      hasItemsInCart: (cart.isMultiCart ? orders.length : selectActiveCartLineItemCount(cart)) > 0,
      // multi-cart will use grand subtotal while control will get subtotal of currently active shef
      subtotal: cart.isMultiCart
        ? selectMultiShefCartSubtotal(cart, orderShefIds)
        : selectSingleShefCartSubtotal(cart, cart.activeCartShefId),
      // shefs we want to checkout
      checkoutShefIds: cart.isMultiCart ? orderShefIds : [cart.activeCartShefId],
      isCurrentShefCartEmpty: selectShefCartIsEmpty(cart, shef.id),
      // TODO: handling the multi-cart case of this is captured in a ticket that is queued up
      // for now it's fine to set to default value of false
      // https://app.asana.com/0/0/1202802276871907/f
      isOrderingMoreThanDeliveryDateCapacity: cart.isMultiCart
        ? false
        : shefDeliveryDateCapacityInfo.isOrderingMoreThanDeliveryDateCapacity,
    };
  });

  const showCheckoutButton = hasItemsInCart && !isOrderingMoreThanDeliveryDateCapacity;

  const setQuantity = useCallback(
    (params: { foodItem: CartFoodItem; quantity: number; location: CartActionLocation; deliveryDate: string }) =>
      dispatch(setLineItemQuantity(isEditingOrder, params)),
    [dispatch, isEditingOrder]
  );
  const setAddOnQuantity = useCallback(
    (params: { shefId: string; addOnItem: ServerAddOnItem; quantity: number }) =>
      dispatch(setAddOnItemQuantity(isEditingOrder, params)),
    [dispatch, isEditingOrder]
  );

  const [, , zipCodeMetaData] = useCachedZipCode();
  const { minimumDiscountedShippingAmount } = zipCodeMetaData;

  const parsedQs = QueryString.parse(location.search);
  const exploreSegmentId = parsedQs[QueryParams.EXPLORE_SEGMENT_ID] as string | undefined;
  const exploreSegmentPos = parsedQs[QueryParams.EXPLORE_SEGMENT_POS] as string | undefined;
  const exploreSegmentRenderedItems = parsedQs[QueryParams.EXPLORE_SEGMENT_RENDERED_ITEMS] as string | undefined;
  const exploreSegmentTotalItems = parsedQs[QueryParams.EXPLORE_SEGMENT_TOTAL_ITEMS] as string | undefined;

  const checkoutQueryString = QueryString.stringify({
    [QueryParams.DATE]: deliveryUrlDate,
    [QueryParams.SHEF]: isEditingOrder ? checkoutShefIds : shef.id,
    [QueryParams.RANK]: QueryString.parse(location.search)[QueryParams.RANK] as string | undefined,
    [QueryParams.EXPLORE_SEGMENT_ID]: exploreSegmentId,
    [QueryParams.EXPLORE_SEGMENT_POS]: exploreSegmentPos,
    [QueryParams.EXPLORE_SEGMENT_RENDERED_ITEMS]: exploreSegmentRenderedItems,
    [QueryParams.EXPLORE_SEGMENT_TOTAL_ITEMS]: exploreSegmentTotalItems,
    ...(isEditingOrder && isMultiCart ? { [QueryParams.TYPE]: CONSUMER_ORDER_TYPE_QUERY_PARAM_JUST_EDITED } : {}),
  });

  const onCheckoutClick = useCallback(() => {
    const isInvalidTrialShefOrder = shefProfile.isTrialShef && subtotal < 15 * 100;
    const shouldUpsellDiscountedDelivery =
      minimumDiscountedShippingAmount !== null &&
      isWorthUpsellingFreeDelivery(subtotal, minimumDiscountedShippingAmount);

    if (isInvalidTrialShefOrder) {
      Notifications.error('Must have a subtotal of at least $15!');
    } else if (shouldUpsellDiscountedDelivery) {
      setShowModal(true);
    } else {
      dispatch(startCheckout({ shefIds: checkoutShefIds }));
      checkout(true);
    }
  }, [checkout, checkoutShefIds, dispatch, minimumDiscountedShippingAmount, shefProfile.isTrialShef, subtotal]);

  // The modal cannot always be successfully displayed!
  // (E.g. it fails for shefs with only side items.)
  // If it fails, it will immediately call onHide. So we must call checkout when onHide happens.
  // Otherwise, when a user clicks "checkout" and the modal fails to appear,
  // they will see nothing happen and will never be able to check out.
  const onFreeDeliveryModalHide = useCallback(() => {
    setShowModal(false);
    checkout(true);
  }, [setShowModal, checkout]);

  const { buttonPath, buttonText } = matchPattern({ isEditingOrder, isMultiCart })
    .with({ isEditingOrder: false }, () => ({
      buttonPath: Routes.CONSUMER_ORDER_CART,
      buttonText: 'View Cart',
    }))
    .with({ isEditingOrder: true, isMultiCart: true }, () => ({
      buttonPath: Routes.CONSUMER_ORDER_FINALIZE,
      buttonText: 'View updated cart',
    }))
    .with({ isEditingOrder: true }, () => ({
      buttonPath: getIdSluggedPath(Routes.CONSUMER_ORDER_EDIT_CART, orderId),
      buttonText: 'View updated cart',
    }))
    .exhaustive();

  const { handleScroll, headerPercentScrolled } = useMultiCartScrollHeader();

  const isPickupOrder = shef.shefProfile.fulfillmentOption === FulfillmentOption.Pickup;

  const { onCartMount } = useDishFistCartPositionContext();
  useEffect(() => {
    if (!cartRef.current) {
      onCartMount();
      // eslint-disable-next-line functional/immutable-data -- Run once
      cartRef.current = true;
    }
  }, [onCartMount]);

  const defaultDeliveryDate = DateTime.now().setZone(shef.timeZone).startOf('day').plus({ days: 1 });

  return (
    <>
      <CartCol isMultiCart={isMultiCart} onScroll={handleScroll} showShadow={isMultiCart}>
        <DesktopCart data-cy='order-cart'>
          {isMultiCart ? (
            <ConsumerOrderMultiCartInfo
              setActiveShefDeliveryDate={setDeliveryDate}
              setAddOnQuantity={setAddOnQuantity}
              isEditingOrder={isEditingOrder}
              headerPercentScrolled={headerPercentScrolled}
              match={match}
            />
          ) : (
            <>
              {isCurrentShefCartEmpty ? (
                <CartCol showShadow={false} isMultiCart={false}>
                  <EmptyCart className='center'>
                    <img src={emptyCart} alt='Empty Cart' />
                    <Paragraph size='16px' color={Colors.GRAY_DARK_10} margin='20px 0 0' bold>
                      Your cart is empty
                      <br />
                      Add items to get started
                    </Paragraph>
                  </EmptyCart>
                </CartCol>
              ) : (
                <>
                  <ConsumerOrderCartInfo
                    publicName={publicName}
                    deliveryDate={deliveryDate ?? defaultDeliveryDate}
                    setFoodItem={setQuantity}
                    setAddOnQuantity={setAddOnQuantity}
                    showUnavailableItems
                    showFullTotals={false}
                    isEditingOrder={isEditingOrder}
                    isPickupOrder={isPickupOrder}
                    fulfillmentOption={shef?.shefProfile?.fulfillmentOption ?? undefined}
                  />

                  {shefProfile.isTrialShef && <TrialShefWarning />}
                  {showCheckoutButton && (
                    <CheckoutButtonContainer data-cy='review-order-button' onClick={onCheckoutClick}>
                      <ConsumerOrderCartButton title={buttonText} quantity={activeCartItemCount} />
                    </CheckoutButtonContainer>
                  )}
                  <SuggestedSidesContainer>
                    <SuggestedSides
                      shefLineItems={shefLineItems}
                      shefTimeZone={shef.timeZone}
                      deliveryDate={deliveryDate ?? defaultDeliveryDate}
                      isEditingOrder={isEditingOrder}
                    />
                  </SuggestedSidesContainer>
                </>
              )}
            </>
          )}
        </DesktopCart>
      </CartCol>
      {showCheckoutButton && (
        <MobileStickyFooter className={`${isMultiCart ? 'hidden-sm' : ''} hidden-md hidden-lg`}>
          {isDishFirst && <DeliveryDateMismatchWarning />}
          <ConsumerOrderCartButton
            title={buttonText}
            quantity={activeCartItemCount}
            to={{ pathname: buttonPath, search: checkoutQueryString }}
          />
        </MobileStickyFooter>
      )}
      {!isMultiCart && (
        <ConsumerOrderFreeDeliveryModal
          shef={shef}
          deliveryDate={deliveryDate ?? defaultDeliveryDate}
          foodItems={foodItems}
          show={showModal}
          // The modal cannot always be successfully displayed! If it fails, it will immediately call onHide.
          onHide={onFreeDeliveryModalHide}
          subtotal={subtotal}
          checkout={checkout}
          match={match}
          location={location}
        />
      )}
    </>
  );
};
